'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@coas/ui/lib';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { Fragment, type ReactNode } from 'react';
import { Spinner } from '~/shared/ui/spinner';
import type { GridConfig } from '../helpers/useGridCols';
import { useVirtualItems } from '../helpers/useVirtualItems';

type VirtualGridProps<T> = React.PropsWithChildren & {
    query: Omit<UseInfiniteQueryResult<unknown, unknown>, 'data'>;
    data: T[];
    gridConfig: GridConfig;
    loadingSource?: ReactNode;
    itemBuilder: (item: T, index: number) => ReactNode;
    skeletonItemBuilder?: (index: number) => ReactNode;
};

export const VirtualGrid = <T,>(props: VirtualGridProps<T>) => {
    const t = useTranslations('productGrid');
    const { data, gridConfig, loadingSource, itemBuilder, skeletonItemBuilder } = props;
    const { isSuccess, isLoading, hasNextPage } = props.query;

    const { containerRef, rows, cols, virtualRows, rowVirtualizer } = useVirtualItems({
        query: props.query,
        data,
        gridConfig,
    });

    return (
        <div ref={containerRef} className={'relative min-h-24 w-full flex-1'}>
            {isLoading && (
                <div
                    className={cn(
                        '-inset-x-page absolute inset-y-0 overflow-hidden max-sm:bg-white',
                        'fade-in animate-in fill-mode-both delay-300',
                    )}
                >
                    <div
                        key={'spinner'}
                        className={'absolute inset-0 m-auto max-h-fit max-w-fit'}
                        style={{
                            transform: 'translateY(calc(-1 * min(2.5rem, 50%)))',
                        }}
                    >
                        <Spinner className={'size-10'} />
                    </div>
                    {!!loadingSource && (
                        <video
                            className={'absolute inset-0 m-auto aspect-video-vertical w-full sm:hidden'}
                            width={1080}
                            height={1920}
                            muted
                            autoPlay
                            loop
                            playsInline
                        >
                            {loadingSource}
                        </video>
                    )}
                </div>
            )}
            {isSuccess && data.length === 0 && (
                <div key={'empty'} className={'absolute inset-0 m-auto flex h-fit flex-col items-center leading-snug'}>
                    <span className={'font-600 text-28'}>{t('empty')}</span>
                    <span className={'text-neutral-400'}>{t('emptyMessage')}</span>
                </div>
            )}
            <div className={'relative'} style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                {virtualRows.map(virtualRow => {
                    const isLoaderRow = virtualRow.index > rows - 1;
                    const rowItems = data.slice(virtualRow.index * cols, (virtualRow.index + 1) * cols);

                    if (isLoaderRow) {
                        return (
                            <div
                                data-virtual-loader
                                key={virtualRow.index}
                                data-index={virtualRow.index}
                                ref={rowVirtualizer.measureElement}
                                className={
                                    'absolute grid w-full items-stretch gap-x-2 py-1.5 md:gap-x-3 md:py-2.5 lg:gap-x-4 lg:py-3'
                                }
                                style={{
                                    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                }}
                            >
                                {skeletonItemBuilder !== undefined &&
                                    Array.from({ length: cols - rowItems.length }).map((_, index) => {
                                        return (
                                            <Fragment key={virtualRow.index * cols + index}>
                                                {skeletonItemBuilder(virtualRow.index * cols + index)}
                                            </Fragment>
                                        );
                                    })}
                            </div>
                        );
                    }

                    return (
                        <div
                            key={virtualRow.index}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            className={
                                'absolute grid w-full items-stretch gap-x-2 py-1.5 md:gap-x-3 md:py-2.5 lg:gap-x-4 lg:py-3'
                            }
                            style={{
                                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                                transform: `translateY(${virtualRow.start}px)`,
                            }}
                        >
                            {rowItems.map((item, index) => {
                                return (
                                    <Fragment key={virtualRow.index * cols + index}>
                                        {itemBuilder(item, virtualRow.index * cols + index)}
                                    </Fragment>
                                );
                            })}
                            {cols > rowItems.length &&
                                hasNextPage &&
                                skeletonItemBuilder !== undefined &&
                                Array.from({ length: cols - rowItems.length }).map((_, index) => {
                                    return (
                                        <Fragment key={virtualRow.index * cols + index}>
                                            {skeletonItemBuilder(virtualRow.index * cols + index)}
                                        </Fragment>
                                    );
                                })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
